import React from 'react'
import { Fragment, jsx, jsxs } from 'react/jsx-runtime'
import { ListItemContentPlugin } from '@udecode/plate-list/react'
import { MentionPlugin } from '@udecode/plate-mention/react'
import { Components, toJsxRuntime } from 'hast-util-to-jsx-runtime'
import rehypePrism from 'rehype-prism-plus'
import remarkGfm from 'remark-gfm'
import markdown from 'remark-parse'
import remarkRehype, { Options } from 'remark-rehype'
import { unified } from 'unified'
import { Channel, useUserLookupContext } from 'use-towns-client'
import { VFile } from 'vfile'

import { TUserIDNameMap } from '../components/plate-ui/autocomplete/types'
import { ELEMENT_MENTION_CHANNEL } from '../plugins/createChannelPlugin'
import { ELEMENT_CONTRACT_ADDRESS } from '../plugins/createContractAddressPlugin'
import { ELEMENT_MENTION_TICKER } from '../plugins/createTickerMentionPlugin'

import remarkDecodeHTMLCodeBlocks from './remark/remarkDecodeHTMLCodeBlocks'
import remarkEditedAnnotation, { ELEMENT_EDITED } from './remark/remarkEditedAnnotation'
import remarkEmoji, { ELEMENT_EMOJI } from './remark/remarkEmoji'
import remarkPreserveCodeLanguage from './remark/remarkPreserveCodeLanguage'
import remarkPreserveListContent from './remark/remarkPreserveListContent'
import remarkRemoveHeadings from './remark/remarkRemoveHeadings'
import remarkTransformUserAndChannels from './remark/remarkTransformUserAndChannels'
import remarkUnderline from './remark/remarkUnderline'
import { getChannelNames } from './helpers'
import {
    channelMentionHandler,
    contractAddressHandler,
    editedHandler,
    emojiHandler,
    listContentHandler,
    tickerMentionHandler,
    userMentionHandler,
} from './rehypeHandlers'

type MarkdownRendererProps = React.PropsWithChildren<{
    isEdited?: boolean
    components: Partial<Components>
    channels?: Channel[]
    userHashMap?: TUserIDNameMap
    lookupUser: ReturnType<typeof useUserLookupContext>['lookupUser']
}>

/**
 * Deserialize content from Markdown format to JSX.
 * This function is only used for displaying message content in timeline,
 *
 * For edit message, use `deserializeMd` function instead.
 * @see deserializeMd
 */
const MarkdownRenderer = ({
    components,
    isEdited = false,
    channels = [],
    userHashMap = {},
    lookupUser,
    children,
}: MarkdownRendererProps): React.ReactNode => {
    if (!children) {
        return null
    }

    const file = new VFile()
    const unifiedPipeline = unified()
        .use(markdown)
        .use(remarkGfm)
        .use(remarkUnderline(children as string))
        .use(remarkPreserveListContent)
        .use(remarkRemoveHeadings)
        .use(remarkDecodeHTMLCodeBlocks)
        .use(remarkEditedAnnotation(isEdited))
        .use(remarkTransformUserAndChannels(channels, userHashMap, lookupUser))
        .use(remarkEmoji)
        .use(remarkPreserveCodeLanguage)
        .use(remarkRehype, {
            passThrough: [
                ListItemContentPlugin.key,
                MentionPlugin.key,
                ELEMENT_MENTION_CHANNEL,
                ELEMENT_MENTION_TICKER,
                ELEMENT_CONTRACT_ADDRESS,
                ELEMENT_EMOJI,
                'abbr',
            ],
            handlers: {
                [MentionPlugin.key]: userMentionHandler,
                [ELEMENT_MENTION_CHANNEL]: channelMentionHandler,
                [ELEMENT_MENTION_TICKER]: tickerMentionHandler,
                [ELEMENT_CONTRACT_ADDRESS]: contractAddressHandler,
                [ListItemContentPlugin.key]: listContentHandler,
                [ELEMENT_EDITED]: editedHandler,
                [ELEMENT_EMOJI]: emojiHandler,
            },
        } as unknown as Options)
        .use(rehypePrism, { ignoreMissing: true, showLineNumbers: false })

    if (typeof children === 'string') {
        file.value = children as string
    } else {
        throw new Error('Expected `children` to be a string')
    }

    const mdastTree = unifiedPipeline.parse(file)
    const hastTree = unifiedPipeline.runSync(mdastTree, file)

    return toJsxRuntime(hastTree, {
        development: true,
        Fragment,
        components,
        ignoreInvalidStyle: true,
        jsx,
        jsxs,
        jsxDEV: jsx,
        passKeys: true,
        passNode: true,
    }) as React.ReactNode
}

/**
 * Compare props to determine if the component should re-render.
 * Given that a lot of transformation is happening here, and this component
 * is rendered multiple times in the timeline we want to be
 * as conservative as possible before re-rendering
 *
 * Unless the MD content or channel/user list has changed, we don't want to re-render
 */
const arePropsEqual = (
    prevProps: MarkdownRendererProps,
    nextProps: MarkdownRendererProps,
): boolean => {
    if (prevProps.children !== nextProps.children) {
        return false
    }

    return getChannelNames(prevProps.channels) === getChannelNames(nextProps.channels)
}

/**
 * @see MarkdownRenderer
 */
const MarkdownToJSX = React.memo(MarkdownRenderer, arePropsEqual)

export default MarkdownToJSX
